import { useSelector } from 'react-redux';

import type { ReviewsSummaryProps } from 'src/components/organisms/ReviewsSummary/components/ReviewsSummary';
import { getAsAdvertisedPercentage } from 'src/components/organisms/ReviewsSummary/helpers/getAsAdvertisedPercentage';
import {
  trackAiSummaryImpression,
  trackMostLovedImpression,
  trackReviewsSummaryImpression,
} from 'src/components/organisms/ReviewsSummary/tracking/reviewsSummaryEvents';
import { selectProductAttributes } from 'src/redux/product/selectors';
import {
  selectAiSummary,
  selectProductInsights,
  selectReviewsAverageRating,
} from 'src/redux/reviews/selectors';
import type { ReviewsSummaryAiSummary } from 'src/redux/reviews/types';

/**
 * View model for the reviews summary. Owns the reads across the product and
 * reviews slices, the minimum-responses rule on the "% as advertised" score
 * and the three impressions, so the presentational section stays pure props.
 *
 * Returns null only when there is nothing at all to show, which is how the
 * section is omitted from the page. Without an AI summary the heading, the
 * rating, the Most Loved pill and the as-advertised score still stand on their own.
 */
export interface ReviewsSummaryViewModel extends Omit<ReviewsSummaryProps, 'aiSummary'> {
  aiSummary: ReviewsSummaryAiSummary | null;
}

export const useReviewsSummaryViewModel = (): ReviewsSummaryViewModel | null => {
  const aiSummary = useSelector(selectAiSummary);
  const productInsights = useSelector(selectProductInsights);
  const attributes = useSelector(selectProductAttributes);
  const averageRating = useSelector(selectReviewsAverageRating);

  const asAdvertisedPercentage = getAsAdvertisedPercentage(productInsights);
  const isMostLoved = Boolean(attributes?.highlyRated);

  if (!aiSummary && averageRating === null && asAdvertisedPercentage === null && !isMostLoved) {
    return null;
  }

  return {
    aiSummary: aiSummary ?? null,
    asAdvertisedPercentage,
    averageRating,
    isMostLoved,
    onAiSummaryVisible: trackAiSummaryImpression,
    onMostLovedVisible: trackMostLovedImpression,
    onVisible: trackReviewsSummaryImpression,
  };
};
